import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface Heatmap168Props {
  /** 168-element array indexed by dow*24 + hour (dow 0 = Monday). */
  cells: number[] | undefined;
  className?: string;
}

export function Heatmap168Hour({ cells, className }: Heatmap168Props) {
  if (!cells || cells.length !== 168) {
    return (
      <div className={cn('flex h-32 items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground', className)}>
        No dow168 histogram yet
      </div>
    );
  }
  const max = Math.max(1, ...cells);

  return (
    <div className={cn('space-y-1', className)}>
      <div className="grid" style={{ gridTemplateColumns: '32px repeat(24, minmax(0, 1fr))' }}>
        <div />
        {Array.from({ length: 24 }).map((_, h) => (
          <div key={h} className="text-center text-[10px] text-muted-foreground">
            {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
          </div>
        ))}
        {DOW_LABELS.map((label, dow) => (
          <FragmentRow key={label} label={label} dow={dow} cells={cells} max={max} />
        ))}
      </div>
    </div>
  );
}

function FragmentRow({ label, dow, cells, max }: { label: string; dow: number; cells: number[]; max: number }) {
  return (
    <>
      <div className="flex items-center justify-end pr-1 text-[10px] text-muted-foreground">{label}</div>
      {Array.from({ length: 24 }).map((_, hour) => {
        const idx = dow * 24 + hour;
        const v = cells[idx] ?? 0;
        const intensity = v / max;
        return (
          <Tooltip key={hour}>
            <TooltipTrigger asChild>
              <div
                aria-label={`${label} ${String(hour).padStart(2, '0')}:00 — ${v}`}
                className="m-[1px] h-4 rounded-[2px]"
                style={{ backgroundColor: 'hsl(var(--primary))', opacity: Math.max(0.07, intensity) }}
              />
            </TooltipTrigger>
            <TooltipContent>{`${label} ${String(hour).padStart(2, '0')}:00 — ${v.toFixed(2)}`}</TooltipContent>
          </Tooltip>
        );
      })}
    </>
  );
}
